/**
 * Scroll-related setup + per-frame UI sync.
 *
 * Where to add new code:
 * - New sticky / scrollToFixed target     → initStickyElements()     (via initScroll)
 * - New ScrollTrigger / scrub section     → new initXxx() next to initRevealExpand → call from initScroll()
 * - Class / UI that tracks scrollY        → handleScroll()            (via initEvents in main.js)
 * - Click / one-shot scroll UX            → bindBackToTop() or new bindXxx() → call from initScroll()
 * - Shared thresholds                     → CONSTANTS below + CONFIG.scrollThreshold in utils.js
 * - App-wide scroll listener wiring       → do NOT addEventListener('scroll') here; use initEvents in utils/main
 *
 * Rule of thumb: setup once → initScroll; react to position → handleScroll; never auto-run on import.
 */

import { CONFIG } from "../utils";

gsap.registerPlugin(ScrollTrigger);

// =========================
// Constants
// =========================
const BACK_TO_TOP_SHOW_AT = 300;
const BACK_TO_TOP_SELECTOR = ".back-to-top, [data-js-action='back-to-top']";
const STICKY_SOCIAL_SELECTOR = ".news-detail .social-network";
const REVEAL_CONTAINER_SELECTOR = ".container-block";
const REVEAL_SECTION_SELECTOR = ".wrapper-reveal";
const REVEAL_ELEMENT_SELECTOR = ".reveal-element";
const REVEAL_CONTENT_SELECTOR = ".txt";
const CLIP_REVEAL_SELECTOR = '[data-js-target="clip-reveal"]';
const CLIP_REVEAL_DEFAULTS = {
	startPercent: 70, // fires once this much of the section has scrolled into view
	scaleDuration: 3, // seconds the zoom runs
	fadeDuration: 0.3, // seconds per layer fade
	swapRatio: 0.7, // share of the zoom done before the layers swap
	contentDelay: 0.1, // seconds the inner content trails the swap
};

// =========================
// Feature inits (setup once)
// =========================

/**
 * Sticky sidebar social network on news detail pages.
 */
export function initStickyElements() {
	try {
		if (!$(STICKY_SOCIAL_SELECTOR).length) return;

		$(STICKY_SOCIAL_SELECTOR).scrollToFixed({
			zIndex: 99,
			marginTop: 100,
			limit: function () {
				return (
					$("footer").offset().top -
					$("header").outerHeight() -
					100
				);
			},
			postUnfixed: function () {
				$(this).addClass("reached-limit");
			},
			preFixed: function () {
				$(this).removeClass("reached-limit");
			},
		});
	} catch (error) {
		console.error("Sticky elements error:", error);
	}
}

/**
 * Full-bleed reveal expand + text fade (GSAP ScrollTrigger).
 * No-ops when required DOM is missing (most pages).
 */
function initRevealExpand() {
	const layoutContainer = document.querySelector(REVEAL_CONTAINER_SELECTOR);
	const sectionTrigger = document.querySelector(REVEAL_SECTION_SELECTOR);

	if (!layoutContainer || !sectionTrigger) return;

	const revealElement = sectionTrigger.querySelector(REVEAL_ELEMENT_SELECTOR);
	const revealContent = sectionTrigger.querySelector(REVEAL_CONTENT_SELECTOR);

	if (!revealElement || !revealContent) return;

	try {
		const containerWidth = parseFloat(
			getComputedStyle(layoutContainer).width
		);

		const isMobile = window.innerWidth < 768;
		const isTablet = window.innerWidth < 1200;

		const settings = {
			// keep original cinematic timing on Full HD/Desktop
			expandStart: isMobile
				? "top 88%"
				: isTablet
					? "top 82%"
					: "top 75%",

			expandEnd: isMobile
				? "top 70%"
				: isTablet
					? "top 45%"
					: "top 5%",

			// text reveal earlier on small screens
			textStart: isMobile ? "top 92%" : "top 85%",

			textEnd: isMobile ? "top 60%" : "top 35%",

			// smoother/faster scrub on mobile
			scrub: isMobile ? 0.7 : 1.2,
		};

		gsap.set(revealElement, {
			width: containerWidth,
		});

		gsap.set(revealContent, {
			y: 80,
			opacity: 0,
		});

		gsap.to(revealElement, {
			width: "100vw",
			ease: "none",
			scrollTrigger: {
				trigger: sectionTrigger,
				start: settings.expandStart,
				end: settings.expandEnd,
				scrub: settings.scrub,
				invalidateOnRefresh: true,
			},
		});

		gsap.to(revealContent, {
			y: 0,
			opacity: 1,
			ease: "power2.out",
			scrollTrigger: {
				trigger: sectionTrigger,
				start: settings.textStart,
				end: settings.textEnd,
				scrub: settings.scrub,
				invalidateOnRefresh: true,
			},
		});
	} catch (error) {
		console.error("Reveal expand error:", error);
	}
}

/**
 * Clip-text reveal: the image-masked heading zooms out, then the stage swaps for
 * the real section content. Runs for every matching section, no-ops without one.
 *
 * Markup:
 *   [data-js-target="clip-reveal"]     ← section (ScrollTrigger trigger)
 *     [data-js-clip="stage"]           ← layer that fades out
 *       [data-js-clip="text"]          ← masked heading that zooms
 *     [data-js-clip="bg"]              ← layer that fades in
 *       [data-js-clip="content"]       ← inner content, fades in just after (optional)
 *
 * Optional overrides on the section:
 *   data-js-start-percent                     ← how much of the section scrolls in first
 *   data-js-scale-duration / data-js-fade-duration / data-js-swap-ratio  ← timing
 */
function initTextClipReveal() {
	document.querySelectorAll(CLIP_REVEAL_SELECTOR).forEach(buildClipReveal);
}

function buildClipReveal(section) {
	const part = (role) => section.querySelector(`[data-js-clip="${role}"]`);
	const text = part("text");
	const stage = part("stage");
	const bg = part("bg");
	const content = part("content");

	if (!text || !stage || !bg) return;

	try {
		const { dataset } = section;
		const setting = (raw, fallback) => {
			const value = parseFloat(raw);
			return Number.isFinite(value) ? value : fallback;
		};

		const scaleDuration = setting(
			dataset.jsScaleDuration,
			CLIP_REVEAL_DEFAULTS.scaleDuration
		);
		const fadeDuration = setting(
			dataset.jsFadeDuration,
			CLIP_REVEAL_DEFAULTS.fadeDuration
		);
		const swapAt =
			scaleDuration *
			setting(dataset.jsSwapRatio, CLIP_REVEAL_DEFAULTS.swapRatio);

		const hiddenLayers = [bg, content].filter(Boolean);

		gsap.set(stage, { opacity: 1, pointerEvents: "auto" });
		gsap.set(hiddenLayers, { opacity: 0 });

		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			gsap.set(stage, { opacity: 0, pointerEvents: "none" });
			gsap.set(hiddenLayers, { opacity: 1 });
			return;
		}

		// Measured against the section, not the viewport, so desktop, tablet and
		// mobile all start at the same point of the section regardless of how
		// tall the screen is: "70% bottom" = 70% of the section has scrolled in.
		const startPercent = setting(
			dataset.jsStartPercent,
			CLIP_REVEAL_DEFAULTS.startPercent
		);

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: section,
				start: `${startPercent}% bottom`,
				toggleActions: "play none none none",
				invalidateOnRefresh: true,
			},
		});

		// Stage and text scale together, so the heading zooms by the ratio squared —
		// that compound zoom is what carries the artwork past the viewport edge.
		// offsetWidth ignores transforms, so it stays correct on every refresh.
		tl.to(
			[text, stage],
			{
				scale: () =>
					text.offsetWidth ? window.innerWidth / text.offsetWidth : 1,
				duration: scaleDuration,
				ease: "power2.inOut",
			},
			0
		);

		const swaps = [
			[stage, { opacity: 0, pointerEvents: "none" }, swapAt],
			[bg, { opacity: 1 }, swapAt],
			[
				content,
				{ opacity: 1 },
				swapAt + CLIP_REVEAL_DEFAULTS.contentDelay,
			],
		];

		swaps.forEach(([element, vars, position]) => {
			if (element) tl.to(element, { ...vars, duration: fadeDuration }, position);
		});
	} catch (error) {
		console.error("Clip reveal error:", error);
	}
}

/**
 * Bind back-to-top click once (not on every scroll tick).
 */
function bindBackToTop() {
	document.querySelectorAll(BACK_TO_TOP_SELECTOR).forEach((backTop) => {
		backTop.addEventListener("click", (e) => {
			e.preventDefault();
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		});
	});
}

// =========================
// Per-scroll UI sync
// =========================

/**
 * Called via initEvents({ onScroll }) — throttled in utils (leading + trailing).
 * Only toggles classes based on scrollY; do not bind listeners here.
 */
export function handleScroll() {
	const scrollTop = window.scrollY;

	document.body.classList.toggle(
		"minimize",
		scrollTop > CONFIG.scrollThreshold
	);

	document.querySelectorAll(BACK_TO_TOP_SELECTOR).forEach((el) => {
		el.classList.toggle("show", scrollTop > BACK_TO_TOP_SHOW_AT);
	});
}

// =========================
// Public boot
// =========================

/**
 * One-time scroll module setup. Call from main via safeInit(initScroll, "scroll").
 */
export function initScroll() {
	initStickyElements();
	initRevealExpand();
	initTextClipReveal();
	bindBackToTop();
}
